// src/components/annotation/Canvas.tsx

import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { drawAnnotations, clearContrastCache } from './utils/drawing'
import { Tag } from '../../types/models/tag'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { MousePosition } from './hooks/useImageAnnotation'
import { isPointInOBB } from '../../utils/helpers'
import Spinner from 'react-bootstrap/Spinner'

export interface CanvasProps {
  canvasRef: RefObject<HTMLCanvasElement>
  imageRef: RefObject<HTMLImageElement>
  imageUrl: string | undefined
  handleMouseEvent: (
    e: React.MouseEvent<HTMLCanvasElement>,
    eventType: 'down' | 'move' | 'up' | 'click' | 'context'
  ) => void

  handleWheel: (e: React.WheelEvent<HTMLCanvasElement>) => void
  zoomLevel: number
  panOffset: MousePosition
  newBoundingBox: AnnotationDataBoundingBox | null
  selectedBBox: AnnotationDataBoundingBox | null
  onBoxSelect: (box: AnnotationDataBoundingBox | null) => void
  tags: { tags: Tag[] } | undefined
  useBBoxes: boolean
  showBBoxes: boolean
  mousePosition: MousePosition
  contrast: number
  // showHeatmap: boolean
  handleDoubleClick: (e: React.MouseEvent<HTMLCanvasElement>) => void
  showConfidence: boolean
  labelMode?: 'class' | 'class+confidence'
  canvasWidth: number //set to 0 to use image size
  canvasHeight: number
  bBoxIndex?: number
  filteredBoxes?: AnnotationDataBoundingBox[]
  hoveredBoxIndex?: number
  // OBB creation helpers
  creationAxisStart?: { x: number; y: number } | null
  creationPhase?: 'idle' | 'axisStart' | 'axisFixed'
  isEditable?: boolean
  onImageLoaded?: () => void
  // AAL Review: Status indicators for accepted/rejected/untouched boxes
  bboxStatuses?: Record<number, 'untouched' | 'accepted' | 'rejected'>
  // Content dimensions from API (may differ from image natural dimensions)
  contentWidth?: number
  contentHeight?: number
}

// (Unused) helper kept for reference; replaced by ResizeObserver responsive sizing
// const calculateCanvasDimensions = (imageWidth: number, imageHeight: number, maxDimension = 768) => {
//   const imageAspectRatio = imageWidth / imageHeight
//   let canvasWidth, canvasHeight
//   if (imageAspectRatio >= 1) {
//     canvasWidth = maxDimension
//     canvasHeight = Math.round(maxDimension / imageAspectRatio)
//   } else {
//     canvasHeight = maxDimension
//     canvasWidth = Math.round(maxDimension * imageAspectRatio)
//   }
//   return { canvasWidth, canvasHeight }
// }

export const Canvas: React.FC<CanvasProps> = ({
  canvasRef,
  imageRef,
  imageUrl,
  handleMouseEvent,
  handleWheel,
  zoomLevel,
  panOffset,
  newBoundingBox,
  selectedBBox,
  onBoxSelect,
  tags,
  useBBoxes,
  mousePosition,
  contrast,
  // showHeatmap,
  showBBoxes,
  handleDoubleClick,
  showConfidence,
  labelMode = 'class+confidence',
  canvasWidth,
  canvasHeight,
  bBoxIndex,
  filteredBoxes,
  hoveredBoxIndex,
  creationAxisStart,
  creationPhase,
  isEditable = true,
  onImageLoaded,
  bboxStatuses,
  contentWidth,
  contentHeight,
}) => {
  // Responsive canvas: occupy the largest square within the container (no hard-coded defaults)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [isImageLoading, setIsImageLoading] = useState(true)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [allowBoundingBoxes, setAllowBoundingBoxes] = useState(false)
  const [isMouseOverCanvas, setIsMouseOverCanvas] = useState(false)
  const [canvasMousePosition, setCanvasMousePosition] = useState<MousePosition>({ x: 0, y: 0 })

  // RAF batching for mouse moves
  const rafIdRef = useRef<number | null>(null)
  const pendingMousePosRef = useRef<MousePosition | null>(null)

  // Handle initial image loading
  useEffect(() => {
    if (imageUrl) {
      setIsImageLoading(true)
      setIsImageLoaded(false)
      setAllowBoundingBoxes(false)
      // Clear contrast cache when image changes for performance
      clearContrastCache()
    }
  }, [imageUrl])

  // Allow bounding boxes after a brief delay once image is loaded
  useEffect(() => {
    if (isImageLoaded) {
      const timer = setTimeout(() => {
        setAllowBoundingBoxes(true)
      }, 50) // Small delay to ensure image is rendered
      return () => clearTimeout(timer)
    }
  }, [isImageLoaded])

  // ResizeObserver to size canvas to fill the entire container
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const updateDimensions = () => {
      const w = el.clientWidth || el.offsetWidth || 0
      const h = el.clientHeight || el.offsetHeight || 0
      if (w > 0 && h > 0) {
        setCanvasDimensions({ width: w, height: h })
      }
    }

    // Set initial dimensions
    updateDimensions()

    const ro = new ResizeObserver((entries) => {
      if (entries && entries.length > 0) {
        const entry = entries[0]
        if (entry && entry.contentRect) {
          const w = Math.floor(entry.contentRect.width)
          const h = Math.floor(entry.contentRect.height)
          if (w > 0 && h > 0) {
            setCanvasDimensions({ width: w, height: h })
          }
        }
      }
      // Fallback: use element dimensions directly
      updateDimensions()
    })
    ro.observe(el)

    return () => ro.disconnect()
  }, [])

  // Memoize drawAnnotations call dependencies for performance
  const drawAnnotationsMemo = useCallback(() => {
    if (!isImageLoaded || !canvasRef.current || !imageRef.current) {
      return
    }
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    // Don't draw if canvas dimensions are invalid (0 or negative)
    // This prevents annotations from appearing in wrong positions before canvas is properly sized
    if (canvas.width <= 0 || canvas.height <= 0) {
      return
    }

    // Also check that image has valid dimensions
    if (imageRef.current.naturalWidth <= 0 || imageRef.current.naturalHeight <= 0) {
      return
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    drawAnnotations(
      ctx,
      canvas,
      imageRef.current,
      zoomLevel,
      panOffset,
      filteredBoxes || [],
      showConfidence,
      tags?.tags || [],
      useBBoxes,
      showBBoxes && allowBoundingBoxes, // Only show bounding boxes if allowed
      newBoundingBox,
      selectedBBox,
      canvasMousePosition, // Use canvas coordinates for crosshairs
      contrast,
      // showHeatmap,
      bBoxIndex,
      hoveredBoxIndex,
      isMouseOverCanvas,
      creationAxisStart || null,
      creationPhase || 'idle',
      isEditable,
      labelMode,
      bboxStatuses,
      contentWidth,
      contentHeight
    )
  }, [
    isImageLoaded,
    allowBoundingBoxes,
    canvasRef,
    imageRef,
    zoomLevel,
    panOffset,
    filteredBoxes,
    tags,
    useBBoxes,
    showBBoxes,
    newBoundingBox,
    selectedBBox,
    canvasMousePosition,
    contrast,
    bBoxIndex,
    hoveredBoxIndex,
    isMouseOverCanvas,
    showConfidence,
    creationAxisStart,
    creationPhase,
    isEditable,
    labelMode,
    bboxStatuses,
    contentWidth,
    contentHeight,
  ])

  // Only draw when image is loaded AND canvas has valid dimensions
  // This prevents annotations from appearing in wrong positions before canvas is properly sized
  useEffect(() => {
    if (isImageLoaded && canvasDimensions.width > 0 && canvasDimensions.height > 0) {
      // Small delay to ensure canvas element attributes are updated
      const timer = setTimeout(() => {
        drawAnnotationsMemo()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [drawAnnotationsMemo, isImageLoaded, canvasDimensions.width, canvasDimensions.height])

  // Force redraw when canvas dimensions change
  useEffect(() => {
    if (isImageLoaded && canvasDimensions.width > 0 && canvasDimensions.height > 0) {
      // Small delay to ensure canvas element attributes are updated
      const timer = setTimeout(() => {
        drawAnnotationsMemo()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [canvasDimensions.width, canvasDimensions.height, isImageLoaded, drawAnnotationsMemo])

  const getCanvasMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): MousePosition => {
      if (!canvasRef.current) return { x: 0, y: 0 }

      const rect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRef.current.width / rect.width
      const scaleY = canvasRef.current.height / rect.height

      const canvasX = (e.clientX - rect.left) * scaleX
      const canvasY = (e.clientY - rect.top) * scaleY

      return { x: canvasX, y: canvasY }
    },
    [canvasRef]
  )

  const getImageMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): MousePosition => {
      if (!canvasRef.current || !imageRef.current) return { x: 0, y: 0 }

      const rect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRef.current.width / rect.width
      const scaleY = canvasRef.current.height / rect.height

      const canvasX = (e.clientX - rect.left) * scaleX
      const canvasY = (e.clientY - rect.top) * scaleY

      // Undo zoom/pan to get world coordinates in pre-transform canvas space
      const centerX = canvasRef.current.width / 2
      const centerY = canvasRef.current.height / 2
      const worldX = (canvasX - centerX) / zoomLevel + centerX - panOffset.x
      const worldY = (canvasY - centerY) / zoomLevel + centerY - panOffset.y

      // Compute contain-fit mapping for current canvas and image
      const imgW = imageRef.current.naturalWidth || 1
      const imgH = imageRef.current.naturalHeight || 1
      const scale = Math.min(canvasRef.current.width / imgW, canvasRef.current.height / imgH)
      const drawW = imgW * scale
      const drawH = imgH * scale
      const offsetX = (canvasRef.current.width - drawW) / 2
      const offsetY = (canvasRef.current.height - drawH) / 2

      // Map world canvas coords back to image coords via inverse of contain mapping
      const imgX = (worldX - offsetX) / scale
      const imgY = (worldY - offsetY) / scale

      return { x: imgX, y: imgY }
    },
    [canvasRef, imageRef, zoomLevel, panOffset.x, panOffset.y]
  )

  // Memoized event handlers
  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // handleMouseEvent already handles box selection with proper coordinate conversion
      // No need to duplicate that logic here
      handleMouseEvent(e, 'down')
    },
    [handleMouseEvent]
  )

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()

      // Get canvas position immediately for responsive feel
      const canvasPos = getCanvasMousePos(e)

      // Store pending mouse position
      pendingMousePosRef.current = canvasPos

      // Batch updates using RAF for smooth 60fps
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null
          if (pendingMousePosRef.current) {
            setCanvasMousePosition(pendingMousePosRef.current)
            pendingMousePosRef.current = null
          }
        })
      }

      handleMouseEvent(e, 'move')
    },
    [handleMouseEvent, getCanvasMousePos]
  )

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [])

  const handleCanvasMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleMouseEvent(e, 'up')
    },
    [handleMouseEvent]
  )

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleMouseEvent(e, 'click')
    },
    [handleMouseEvent]
  )

  const handleCanvasContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleMouseEvent(e, 'context')
    },
    [handleMouseEvent]
  )

  const handleImageLoad = useCallback(() => {
    if (!canvasRef.current || !imageRef.current) {
      return
    }

    setIsImageLoaded(true)
    setIsImageLoading(false)

    // Notify parent that image is loaded
    if (onImageLoaded) {
      onImageLoaded()
    }
  }, [canvasRef, imageRef, onImageLoaded])

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={Math.round(canvasDimensions.width * (window.devicePixelRatio || 1))}
        height={Math.round(canvasDimensions.height * (window.devicePixelRatio || 1))}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onClick={handleCanvasClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleCanvasContextMenu}
        onWheel={handleWheel}
        style={{
          userSelect: 'none',
          width: `${canvasDimensions.width}px`,
          height: `${canvasDimensions.height}px`,
          display: 'block',
          visibility: isImageLoading ? 'hidden' : 'visible',
          // UX: Hardware acceleration for smooth interactions (like Labelbox)
          willChange: 'transform',
          transform: 'translateZ(0)',
          // UX: Instant cursor feedback
          touchAction: 'none',
        }}
        onMouseEnter={() => setIsMouseOverCanvas(true)}
        onMouseLeave={() => setIsMouseOverCanvas(false)}
        role="img"
        aria-label="Annotation Canvas"
      />
      <img
        ref={imageRef}
        src={imageUrl}
        alt="Annotate"
        style={{ display: 'none' }}
        onLoad={handleImageLoad}
        onError={(e) => {
          console.error('[Canvas] Image load error:', e, { imageUrl: imageUrl?.substring(0, 100) })
        }}
        crossOrigin="anonymous"
      />
      {isImageLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10,
          }}
          aria-live="polite"
        >
          <Spinner animation="border" variant="light" role="status" aria-label="Loading image">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}
    </div>
  )
}

export default Canvas
